import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { clearServiceWorkerImageCache } from "@/lib/sw";

const AuthContext = createContext(null);

/** Wraps an auth-mutating API call and normalises the response shape. */
async function authRequest(method, path, body) {
  try {
    const { data } = await api[method](path, body);
    return { ok: true, user: data };
  } catch (e) {
    return { ok: false, error: formatError(e) };
  }
}

function useSessionLoader(setUser) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) setUser(false);
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [setUser]);
  return ready;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // null = loading, false = guest, obj = user
  const ready = useSessionLoader(setUser);

  const login = useCallback(async (email, password) => {
    const res = await authRequest("post", "/auth/login", { email, password });
    if (res.ok) setUser(res.user);
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  }, []);

  const register = useCallback(async (name, email, password) => {
    const res = await authRequest("post", "/auth/register", { name, email, password });
    if (res.ok) setUser(res.user);
    return res.ok ? { ok: true } : { ok: false, error: res.error };
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.warn("[auth] logout error (ignoring):", err?.message || err); }
    await clearServiceWorkerImageCache();
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, ready, login, register, logout }),
    [user, ready, login, register, logout],
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
